import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Route: /subscription/revolut-success
//
// Two ways to land here:
//  1. Inside the Revolut popup (opened by PlanSelected) → tell the opener and close.
//  2. Same tab (popup blocked, Revolut redirected back) → verify, log in, go to app.

const MAX_ATTEMPTS = 10;
const RETRY_MS     = 3000;

const RevolutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("Confirming your payment...");
  const [failed, setFailed] = useState(false);
  const startedRef = useRef(false);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    // Case 1: we're in the popup — hand control back to the main window
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage({ type: "revolut-checkout-complete" }, window.location.origin);
      } catch {
        /* ignore */
      }
      setStatus("Payment received. You can close this window.");
      setTimeout(() => window.close(), 800);
      return;
    }

    // Case 2: same tab
    const pending = JSON.parse(sessionStorage.getItem("pendingRegistration") || "null");
    const subscriptionId = searchParams.get("subscriptionId") || pending?.subscriptionId;

    if (!subscriptionId) {
      setFailed(true);
      setStatus("We couldn't find your subscription. Sign in to check your billing status.");
      return;
    }

    let attempts = 0;

    const verify = async () => {
      attempts += 1;
      try {
        const res = await fetch(
          `${API_URL}/api/subscription/verify-revolut-session?subscriptionId=${encodeURIComponent(subscriptionId)}`,
        );
        const result = await res.json();

        if (res.ok && result.success) {
          // The app reads the session from "authToken" — overwrite it so the
          // previous user's session can't leak into this new account.
          localStorage.removeItem("token");
          localStorage.setItem("authToken", result.token);
          localStorage.setItem("user", JSON.stringify(result.user));
          sessionStorage.removeItem("pendingRegistration");
          navigate("/login", { replace: true });
          return;
        }

        // First payment still processing → state stays "pending" briefly
        if (result.state === "pending" && attempts < MAX_ATTEMPTS) {
          setTimeout(verify, RETRY_MS);
          return;
        }

        setFailed(true);
        setStatus(result.message || "Payment not completed.");
      } catch (err) {
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(verify, RETRY_MS);
          return;
        }
        setFailed(true);
        setStatus("We couldn't confirm your payment. Sign in to check your billing status.");
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        fontFamily: "'DM Sans', sans-serif",
        color: "#0F172A",
        padding: 24,
        textAlign: "center",
      }}
    >
      <p style={{ fontSize: 16, margin: 0 }}>{status}</p>
      {failed && (
        <button
          onClick={() => navigate("/login")}
          style={{
            background: "#2563EB",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          Go to sign in
        </button>
      )}
    </div>
  );
};

export default RevolutSuccess;